import { NotificationDisplayDomain } from '../redux/api/notificationsApi';
import type { IUserNotificationItem } from '../redux/api/notificationsApi';
import { Paths } from '../shared/paths';

function pickId(
  data: Record<string, unknown> | null | undefined,
  keys: string[],
): string | undefined {
  if (!data) {
    return undefined;
  }
  for (const key of keys) {
    const v = data[key];
    if (typeof v === 'string' && v.length > 0) {
      return v;
    }
    if (typeof v === 'number' && Number.isFinite(v)) {
      return String(v);
    }
  }
  return undefined;
}

function fallbackPathByDomain(
  domain: NotificationDisplayDomain,
  currentUserId?: string,
): string | null {
  switch (domain) {
    case NotificationDisplayDomain.training:
    case NotificationDisplayDomain.workout:
    case NotificationDisplayDomain.reminder:
    case NotificationDisplayDomain.calendar:
      return Paths.trainings.toMy;
    case NotificationDisplayDomain.program:
      return Paths.programs.toList;
    case NotificationDisplayDomain.community:
      return Paths.communities.toList;
    case NotificationDisplayDomain.exercise:
      return Paths.exercises.toList;
    case NotificationDisplayDomain.post:
    case NotificationDisplayDomain.comment:
      if (currentUserId) {
        return Paths.users.toUser(currentUserId);
      }
      return null;
    case NotificationDisplayDomain.user:
    case NotificationDisplayDomain.subscription:
    case NotificationDisplayDomain.relation:
      return Paths.users.toList;
    case NotificationDisplayDomain.chat:
      return Paths.chats;
    case NotificationDisplayDomain.bugReport:
      return Paths.bugReports.root;
    case NotificationDisplayDomain.marketing:
      return Paths.news.root;
    default:
      return null;
  }
}

function withPostCommentQuery(
  path: string,
  record?: Record<string, unknown>,
): string {
  if (!record) {
    return path;
  }
  const postId = pickId(record, ['postId']);
  const commentId = pickId(record, ['commentId']);
  if (!postId && !commentId) {
    return path;
  }
  const searchParams = new URLSearchParams();
  if (postId) {
    searchParams.set('postId', postId);
  }
  if (commentId) {
    searchParams.set('commentId', commentId);
  }
  const search = searchParams.toString();
  return search ? `${path}?${search}` : path;
}

export function resolveNotificationNavigationPath(
  item: Pick<IUserNotificationItem, 'displayDomain' | 'data'>,
  context?: { currentUserId?: string },
): string | null {
  const data = item.data;
  const record =
    data && typeof data === 'object'
      ? (data as Record<string, unknown>)
      : undefined;

  const trainingId = pickId(record, ['trainingId']);
  if (trainingId) {
    return Paths.trainings.toViewId(trainingId);
  }

  const programItemId = pickId(record, ['programItemId']);
  if (programItemId) {
    return Paths.programs.toViewItemId(programItemId);
  }

  const subscriberUserId = pickId(record, ['subscriberUserId']);
  if (subscriberUserId) {
    return Paths.users.toUser(subscriberUserId);
  }

  const wallOwnerId = pickId(record, ['wallOwnerId']);
  if (wallOwnerId) {
    const wallOwnerType = pickId(record, ['wallOwnerType']);
    const wallPath =
      wallOwnerType === 'community'
        ? `${Paths.communities.root}/${wallOwnerId}`
        : context?.currentUserId && wallOwnerId === context.currentUserId
          ? Paths.users.me
          : Paths.users.toUser(wallOwnerId);
    return withPostCommentQuery(wallPath, record);
  }

  const userId = pickId(record, ['userId', 'targetUserId', 'wallUserId']);
  if (userId) {
    return withPostCommentQuery(Paths.users.toUser(userId), record);
  }

  const communityId = pickId(record, ['communityId']);
  if (communityId) {
    return withPostCommentQuery(
      `${Paths.communities.root}/${communityId}`,
      record,
    );
  }

  const exerciseId = pickId(record, ['exerciseId']);
  if (exerciseId) {
    return Paths.exercises.toSummaryId(exerciseId);
  }

  const postId = pickId(record, ['postId']);
  if (postId && context?.currentUserId) {
    return withPostCommentQuery(
      Paths.users.toUser(context.currentUserId),
      record,
    );
  }

  return fallbackPathByDomain(item.displayDomain, context?.currentUserId);
}
